/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useMemo } from 'react';
import { trackAmplitudeEvent } from '../../../../../thirdPartyTracking/amplitude';
import {
  Text,
  Switch,
  Tooltip,
  Icon,
  icons,
  Flex,
} from '@tapresearch/ui/components';
import { Serie } from '@nivo/line';
import { Tooltips } from '../../../../../../shared/Tooltip/Tooltip.styled';

import { Campaign } from '../../../../../api';
import { ResponsiveLineChart } from './ResponsiveLineChart';
import { CHART_CONTROL_TYPES } from './CustomControlLegend';

export interface ResponsiveRatingLineChartSwitchProps {
  isChecked: boolean;
  onChange: () => void;
  answersCount: number;
}

export interface ResponsiveRatingLineChartProps {
  data: Serie[];
  waves: Campaign[];
  legendDirection?: 'row' | 'column';
  customLegendControlProps: ResponsiveRatingLineChartSwitchProps;
  usePercentage?: boolean;
}

export function ResponsiveRatingLineChartSwitch({
  isChecked,
  onChange,
  answersCount,
}: ResponsiveRatingLineChartSwitchProps) {
  return (
    <Flex mr={12} pt={4} alignItems="center">
      <Tooltip
        {...Tooltips.Base}
        label={
          'This toggle groups your top 2 & bottom 2 answers into their own lines on the graph. When there are 5 or more answers you will also see a line for all answers between top and bottom. Turn toggle off to see all individual answers.'
        }
        shouldWrapChildren
        fontSize="md"
        placement="left-start"
      >
        <Icon
          wrapAs={Flex}
          icon={icons.md.InfoOutline}
          boxSize={4}
          color="gray.500"
          mr={1}
        />
      </Tooltip>
      <Flex alignItems="center">
        <Text fontSize="sm" mr={4}>
          Group answers:
        </Text>
        <Switch
          isChecked={isChecked}
          onChange={trackAmplitudeEvent(onChange, {
            eventType: 'groupAnswers',
          })}
          isDisabled={answersCount < 5}
        />
      </Flex>
    </Flex>
  );
}

export function ResponsiveRatingLineChart({
  data,
  waves,
  legendDirection,
  customLegendControlProps,
  usePercentage,
}: ResponsiveRatingLineChartProps) {
  const sortedData = useMemo(
    () =>
      legendDirection === 'row' // Rating chart is ungrouped when direction is set to row
        ? [...data].sort((a, b) => {
            // Ensure the legend is ordered low-to-high
            return parseInt(a.id as string) - parseInt(b.id as string);
          })
        : data,
    [data, legendDirection]
  );
  return (
    <ResponsiveLineChart
      data={sortedData}
      waves={waves}
      usePercentage={usePercentage}
      customLegendControlProps={customLegendControlProps}
      customLegendType={CHART_CONTROL_TYPES.RATING_SCALE_GROUP_ANSWERS}
      {...(legendDirection && {
        legendDirection,
      })}
      {...{
        colorScheme: 'red_blue',
      }}
    />
  );
}
